// src/pages/PostDetail.jsx
import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FiHeart, FiEye, FiArrowLeft } from "react-icons/fi";
import API from "../utils/axiosInstance";

export default function PostDetail() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();

  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  const pingedRef = useRef(false);
  const likingRef = useRef(false);

  // ---- Detay verisini çek ----
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await API.get(`/api/guncel/posts/${id}/`);
        if (!alive) return;
        setPost(normalize(res.data, t));
      } catch (e) {
        console.error("detail fetch error:", e);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [id]);

  // ---- Görüntülenme ping (sayfaya doğrudan gelindiyse de 1 kez) ----
  useEffect(() => {
    if (!post || pingedRef.current) return;
    pingedRef.current = true;

    API.post(`/api/guncel/posts/${post.id}/view/`)
      .then((res) => {
        const v = res?.data?.views_count;
        setPost((p) =>
          p
            ? {
                ...p,
                views_count:
                  typeof v === "number" ? v : p.views_count + 1,
              }
            : p
        );
      })
      .catch(() => {
        /* sessiz */
      });
  }, [post]);

  // ---- Like toggle ----
  async function toggleLike() {
    if (!post || likingRef.current) return;

    // Misafir için anında uyarı (UX)
    const logged =
      (typeof window !== "undefined" &&
        (localStorage.getItem("isLoggedIn") === "1" ||
          !!localStorage.getItem("accessToken"))) || false;
    if (!logged) {
      alert(t("postDetail.loginToLike"));
      return;
    }

    try {
      likingRef.current = true;
      const res = await API.post(`/api/guncel/posts/${post.id}/like_toggle/`);
      const { liked, likes_count } = res.data || {};
      setPost((p) =>
        p
          ? {
              ...p,
              is_liked: !!liked,
              likes_count:
                typeof likes_count === "number"
                  ? likes_count
                  : p.likes_count,
            }
          : p
      );
    } catch (e) {
      if (e?.response?.status === 401) {
        alert(t("postDetail.loginToLike"));
      } else {
        console.error("detail like error:", e);
        alert(t("postDetail.likeFailed"));
      }
    } finally {
      likingRef.current = false;
    }
  }

  // ---- Geri butonu: önce tarihçe geri, yoksa /guncel ----
  function handleBack() {
    if (window.history.length > 1) navigate(-1);
    else navigate("/guncel");
  }

  if (loading)
    return (
      <div className="page">
        <div className="share__empty">{t("postDetail.loading")}</div>
      </div>
    );
  if (!post)
    return (
      <div className="page">
        <div className="share__empty">{t("postDetail.notFound")}</div>
        <div style={{ marginTop: 12 }}>
          <Link to="/guncel" className="btn btn-ghost">
            <FiArrowLeft style={{ marginRight: 6 }} />
            {t("postDetail.backToGuncel")}
          </Link>
        </div>
      </div>
    );

  const img = toAbsoluteMedia(post.image_url);

  return (
    <div className="page">
      {/* Başlık / Geri butonu */}
      <header
        className="postdetail__header"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: 16,
        }}
      >
        <button
          type="button"
          onClick={handleBack}
          className="btn btn-ghost"
          title={t("postDetail.back")}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            // marka tonu: lacivert/pastel
            color: "var(--brand-800, #0f3e70)",
            border: "1px solid var(--brand-200, #c6e4ff)",
            background: "var(--brand-50, #f3f9ff)",
            padding: "8px 12px",
            borderRadius: 10,
          }}
        >
          <FiArrowLeft />
          <span>{t("postDetail.back")}</span>
        </button>

        <h1
          className="postdetail__title"
          style={{
            margin: 0,
            color: "var(--brand-900, #0a2a52)",
            fontSize: 22,
            fontWeight: 700,
          }}
        >
          {post.title}
        </h1>
      </header>

      {/* Görsel */}
      {img && (
        <div style={{ marginBottom: 16 }}>
          <img
            src={img}
            alt={post.title}
            style={{
              width: "100%",
              maxHeight: 440,
              objectFit: "cover",
              borderRadius: 16,
              border: "1px solid var(--brand-100, #e7f3ff)",
              boxShadow: "0 4px 18px rgba(15,62,112,0.08)",
            }}
            onError={(ev) => {
              ev.currentTarget.style.display = "none";
            }}
          />
        </div>
      )}

      {/* Yazar/Firma */}
      <div
        style={{
          color: "var(--brand-800, #0f3e70)",
          marginBottom: 10,
          fontWeight: 600,
        }}
      >
        {post.author_display}
      </div>

      {/* İçerik */}
      <article
        style={{
          color: "var(--brand-900, #0a2a52)",
          lineHeight: 1.7,
          marginBottom: 20,
          whiteSpace: "pre-wrap",
          background: "var(--brand-50, #f3f9ff)",
          border: "1px solid var(--brand-100, #e7f3ff)",
          padding: 16,
          borderRadius: 12,
        }}
      >
        {post.content}
      </article>

      {/* Sayaçlar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 16,
          color: "var(--brand-800, #0f3e70)",
        }}
      >
        <span
          title={t("postDetail.views")}
          style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
        >
          <FiEye />
          <span>{post.views_count}</span>
        </span>

        <button
          type="button"
          onClick={toggleLike}
          className={`chip ${post.is_liked ? "chip--active" : ""}`}
          title={post.is_liked ? t("postDetail.unlike") : t("postDetail.like")}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            borderRadius: 999,
            padding: "6px 12px",
            border: `1px solid ${
              post.is_liked ? "var(--brand-600, #1f79bf)" : "var(--brand-200, #c6e4ff)"
            }`,
            background: post.is_liked
              ? "var(--brand-100, #e7f3ff)"
              : "var(--brand-50, #f3f9ff)",
            color: "var(--brand-800, #0f3e70)",
            cursor: "pointer",
          }}
        >
          <FiHeart />
          <span>{post.likes_count}</span>
        </button>
      </div>
    </div>
  );
}

function normalize(p, t) {
  return {
    ...p,
    image_url: p?.image_url || p?.image || null,
    author_display:
      p?.author_company_name?.trim?.() ||
      (p?.author_role === "supplier" ? t("postDetail.supplier") : t("postDetail.userFallback")),
    views_count: p?.views_count ?? 0,
    likes_count: p?.likes_count ?? 0,
    is_liked: !!p?.is_liked,
  };
}

function toAbsoluteMedia(url) {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith("/")) return url;
  return `/media/${url.replace(/^\/+/, "")}`;
}